import React, { useEffect, useMemo, useState } from "react";

/** Etiquetas que en HTML suelen implicar salto de línea al serializar para máquina de escribir. */
const TYPEWRITER_BLOCK_TAGS = new Set([
	"address", "article", "aside", "blockquote", "body", "canvas", "dd", "div", "dl", "dt",
	"fieldset", "figcaption", "figure", "footer", "form", "h1", "h2", "h3", "h4", "h5", "h6",
	"header", "hr", "li", "main", "nav", "noscript", "ol", "p", "pre", "section", "table",
	"td", "th", "tfoot", "thead", "tr", "ul", "video",
]);

function styleAttributeActsAsBlock(el) {
	if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;
	const st = el.getAttribute("style");
	if (!st || typeof st !== "string") return false;
	return /display\s*:\s*(block|flex|grid|table|flow-root)\b/i.test(st);
}

function elementSerializesAsBlock(el) {
	const tag = el.tagName.toLowerCase();
	if (TYPEWRITER_BLOCK_TAGS.has(tag)) return true;
	if (tag === "span" || tag === "font" || tag === "a") return styleAttributeActsAsBlock(el);
	return false;
}

function serializeNodeForTypewriter(node) {
	if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || "";
	if (node.nodeType !== Node.ELEMENT_NODE) return "";
	const el = node;
	if (el.tagName.toLowerCase() === "br") return "\n";

	if (elementSerializesAsBlock(el)) {
		let inner = "";
		for (let i = 0; i < el.childNodes.length; i++) {
			inner += serializeNodeForTypewriter(el.childNodes[i]);
		}
		if (inner === "") return "";
		return inner.endsWith("\n") ? inner : `${inner}\n`;
	}

	let out = "";
	for (let i = 0; i < el.childNodes.length; i++) {
		out += serializeNodeForTypewriter(el.childNodes[i]);
	}
	return out;
}

function normalizeTypewriterPlain(s) {
	return String(s)
		.replace(/\r\n/g, "\n")
		.replace(/\r/g, "\n")
		.replace(/\n{3,}/g, "\n\n")
		.replace(/\n+$/g, "");
}

export function htmlToPlainTextForTypewriter(html) {
	if (html == null) return "";
	const s = String(html);
	if (typeof document === "undefined") {
		return normalizeTypewriterPlain(
			s
				.replace(/<br\s*\/?>/gi, "\n")
				.replace(/<\/(p|div|h[1-6]|li|tr)\s*>/gi, "\n")
				.replace(/<[^>]+>/g, "")
		);
	}
	try {
		const root = document.createElement("div");
		root.innerHTML = s;
		let out = "";
		for (let i = 0; i < root.childNodes.length; i++) {
			out += serializeNodeForTypewriter(root.childNodes[i]);
		}
		return normalizeTypewriterPlain(out);
	} catch (_) {
		return normalizeTypewriterPlain(
			s
				.replace(/<br\s*\/?>/gi, "\n")
				.replace(/<\/(p|div|h[1-6]|li|tr)\s*>/gi, "\n")
				.replace(/<[^>]+>/g, "")
		);
	}
}

export function useTypewriterPlainText(fullText, durationSec, resetKey) {
	const len = fullText?.length ?? 0;
	const [count, setCount] = useState(0);
	const active = Number(durationSec) > 0 && len > 0;

	useEffect(() => {
		if (!active) {
			setCount(len);
			return undefined;
		}
		setCount(0);
		const totalMs = Math.max(300, Number(durationSec) * 1000);
		const steps = Math.max(1, len);
		const stepMs = Math.max(16, totalMs / steps);
		let current = 0;
		const id = window.setInterval(() => {
			current += 1;
			const next = Math.min(current, len);
			setCount(next);
			if (next >= len) window.clearInterval(id);
		}, stepMs);
		return () => window.clearInterval(id);
	}, [active, len, durationSec, resetKey, fullText]);

	const shown = useMemo(() => (fullText || "").slice(0, active ? count : len), [fullText, active, count, len]);
	return shown;
}

export function EscapeRoomTypewriterTextDiv({ html, durationSec, resetKey, className, style }) {
	const plain = useMemo(() => htmlToPlainTextForTypewriter(html), [html]);
	const text = useTypewriterPlainText(plain, durationSec, resetKey);
	return (
		<div
			className={className}
			style={{
				...style,
				whiteSpace: "pre-wrap",
				wordBreak: "normal",
				overflowWrap: "break-word",
			}}
		>
			{text}
		</div>
	);
}
